import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import { COLORS, DIGITAL_SERVICE_PROVIDERS, DSP_NAMES_FORMATTED } from 'config/constants';
import useBreakpoint from 'hooks/useBreakpoint';
import { kFormatter } from 'helpers/text';

import Flex from 'components/Flex';
import { EllipsisButton } from 'components/Button';
import ActionMenu, { ActionMenuHeader } from 'components/ActionMenu';
import { DspIcon, PeopleIcon } from 'components/Icons';
import EntityElement from 'components/EntityElement';
import MenuToggler from 'components/MenuToggler';
import { DropdownMenuItem, StyledDropdownMenu } from 'components/DropdownMenu';

const ENTITY_PROPS = {
  size: EntityElement.SIZES.SMALL,
  type: EntityElement.TYPES.DSP_ARTIST_PROFILE
};
const FollowerCount = styled(Flex)`
  font-size: 1.1rem;
`;

const DspUrl = styled.span`
  color: ${COLORS.GRAY_MEDIUM};
  font-size: 1.2rem;
  font-weight: 300;
`;

const getProfileData = (profile, service, isSmallScreen) => {
  switch (service) {
    case DIGITAL_SERVICE_PROVIDERS.SOUNDCLOUD:
      return {
        title: profile.username,
        urlText: isSmallScreen ? `/${profile.value}` : profile.dsp_url,
        url: profile.dsp_url
      };

    case DIGITAL_SERVICE_PROVIDERS.APPLE_MUSIC:
    case DIGITAL_SERVICE_PROVIDERS.SPOTIFY:
      return {
        title: profile.value,
        urlText: profile.dsp_url,
        url: profile.dsp_url
      };

    default:
      return profile;
  }
};

export const DspProfileRow = ({ profile, service }) => {
  const isSmallScreen = useBreakpoint(
    useBreakpoint.BREAKPOINTS.SMALL,
    useBreakpoint.DIRECTIONS.DOWN
  );

  const serviceName = DSP_NAMES_FORMATTED[service];
  const actions = [
    {
      name: `View ${serviceName} profile`,
      newTab: true,
      to: profile.dsp_url,
      icon: <DspIcon service={service} size={20} color="currentColor" />,
      showIcon: isSmallScreen
    }
  ];

  const { title, urlText } = getProfileData(profile, service, isSmallScreen);

  return (
    <Flex style={{ width: '100%' }}>
      <EntityElement
        {...ENTITY_PROPS}
        title={title}
        data={profile}
        enableLinks={false}
        subtitle={<DspUrl>{urlText}</DspUrl>}
        lazy={false}
      >
        <Flex grow={0}>
          {profile.followers_count && (
            <FollowerCount
              grow={0}
              title={`${serviceName} followers`}
              align="center"
              className="mr-1"
            >
              <PeopleIcon className="mr-1" size={18} color={COLORS.GRAY_LIGHT} />
              {kFormatter(profile.followers_count)}
            </FollowerCount>
          )}

          <MenuToggler
            renderToggle={({ toggle }) => (
              <EllipsisButton
                onClick={e => {
                  e.stopPropagation();
                  toggle(e);
                }}
              />
            )}
            renderActionMenu={({ isOpen, close }) => (
              <ActionMenu isOpen={isOpen} close={close} actions={actions}>
                <ActionMenuHeader>
                  <EntityElement
                    {...ENTITY_PROPS}
                    enableLinks={false}
                    title={title}
                    data={profile}
                    subtitle={urlText}
                    lazy={false}
                  />
                </ActionMenuHeader>
              </ActionMenu>
            )}
            renderPopoverContent={() => (
              <StyledDropdownMenu>
                {actions.map(action => (
                  <DropdownMenuItem key={action.name} {...action}>
                    {action.name}
                  </DropdownMenuItem>
                ))}
              </StyledDropdownMenu>
            )}
          />
        </Flex>
      </EntityElement>
    </Flex>
  );
};

DspProfileRow.propTypes = {
  profile: PropTypes.shape(),
  service: PropTypes.string
};

DspProfileRow.Tombstone = () => (
  <Flex align="center">
    <EntityElement.Tombstone {...ENTITY_PROPS} />
  </Flex>
);
